import React, { useState, useEffect } from 'react';
import { Heart, ShoppingCart, Plus, Package2, Star, Clock, ChevronLeft, Brain, Scan, ArrowRightLeft, Camera, CheckCircle, X, FolderPlus, Trash2, Pencil } from 'lucide-react';
import AddProductForm from './AddProductForm';
import ProductAnalysisButton from './ProductAnalysisButton';
import ProductDetail from './ProductDetail';
import ShelfView from './ShelfView';
import ShelfMenu from './ShelfMenu';
import ProductAnalysisView from './ProductAnalysisView';
import ProductComparisonView from './ProductComparisonView';
import CatalogProductImage from './CatalogProductImage';

// Component to handle product images with error handling
const ProductImage = ({ product }) => {
  const imageUrl = product.image || product.image_base64;
  
  // Check if we have a valid image (URL or base64)
  const hasValidImage = imageUrl && 
    typeof imageUrl === 'string' && 
    imageUrl.trim() !== '' &&
    (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('/') || imageUrl.startsWith('data:image'));
  
  return (
    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F3D5B5]/20 flex-shrink-0 flex items-center justify-center">
      <CatalogProductImage src={hasValidImage ? imageUrl : ''} alt={product.name} brand={product.brand} className="w-full h-full object-cover" />
    </div>
  );
};

const ProductCatalog = ({
  onBack,
  userId,
  savedProducts = {},
  saveProduct,
  updateProduct,
  deleteProduct,
  customListNames = { custom1: 'My Custom List 1', custom2: 'My Custom List 2' },
  createCustomList,
  renameCustomList,
  deleteCustomList,
  onContinueToChat,
  onOpenRoutine,
  isAnonymous = false,
  onShowSignIn,
  onGatedAction,
  isProductsLoading = false,
  initialView = 'lists',
  initialAnalysisProduct = null,
  // When set (e.g. 'current', 'wishlist', 'custom1'), the analysis view
  // saves straight to that list when the user hits Save to Catalogue. Used
  // by the "Take a picture" button in the add-to-list modal so the user
  // doesn't have to pick the list twice.
  initialAnalyseTargetList = null,
}) => {
  const [currentView, setCurrentView] = useState(initialView);
  const [analysisProduct, setAnalysisProduct] = useState(initialAnalysisProduct);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sourceListId, setSourceListId] = useState('current'); // which list we opened detail from (for correct Back)
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  // Rename custom list: hotfix C. Opens an inline modal pre-filled with the
  // current name; on save, awaits renameCustomList (which persists to the
  // user_lists table). Active when editingListId is non-null.
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState('');
  const [renameListError, setRenameListError] = useState('');
  const [addListError, setAddListError] = useState('');
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [menuProduct, setMenuProduct] = useState(null); // ⋮ quick-actions target (Shelf, #26)

  // Sync selectedProduct when savedProducts updates (e.g., after product update)
  useEffect(() => {
    if (selectedProduct?.id) {
      // Find the updated product in savedProducts
      let updatedProduct = null;
      Object.keys(savedProducts).forEach(category => {
        if (Array.isArray(savedProducts[category])) {
          const found = savedProducts[category].find(p => p.id === selectedProduct.id);
          if (found) {
            updatedProduct = found;
          }
        }
      });
      
      // Update if product was found (React will handle re-render optimization)
      if (updatedProduct) {
        setSelectedProduct(updatedProduct);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProducts]);

  // Handle both object and array formats for savedProducts
  const getProductsByCategory = (category) => {
    if (Array.isArray(savedProducts)) {
      return savedProducts.filter(product => product.list_category === category) || [];
    } else if (savedProducts && typeof savedProducts === 'object') {
      return savedProducts[category] || [];
    }
    return [];
  };

  // Use actual saved products from props
  const wishlistProducts = getProductsByCategory('wishlist');

  // Create custom list using parent's slots (custom1 / custom2).
  // createCustomList is async (it awaits the Supabase persist + surfaces
  // errors) — must await here so listKey is a string, not a Promise.
  const handleCreateCustomList = async () => {
    setAddListError('');
    if (!newListName.trim()) return;
    const listKey = createCustomList ? await createCustomList(newListName.trim()) : null;
    if (listKey) {
      setNewListName('');
      setShowAddListModal(false);
      setCurrentView(listKey);
    } else {
      setAddListError("You've reached the limit (2 custom lists). Delete one to add a new list.");
    }
  };

  const handleDeleteCustomList = (listId) => {
    if (deleteCustomList) deleteCustomList(listId);
    if (currentView === listId) setCurrentView('lists');
  };

  // Hotfix C: rename a custom list. renameCustomList is async (awaits the
  // user_lists upsert and surfaces errors). We pre-fill with the current name
  // and let the user edit; empty / unchanged values are no-ops.
  const openRenameListModal = (listId) => {
    const currentName =
      listId === 'custom1' ? (customListNames.custom1 || 'My Custom List 1') :
      listId === 'custom2' ? (customListNames.custom2 || 'My Custom List 2') :
      '';
    setEditingListId(listId);
    setEditingListName(currentName);
    setRenameListError('');
  };

  const closeRenameListModal = () => {
    setEditingListId(null);
    setEditingListName('');
    setRenameListError('');
  };

  const handleRenameCustomList = async () => {
    setRenameListError('');
    const trimmed = editingListName.trim();
    if (!trimmed) {
      setRenameListError('Name cannot be empty.');
      return;
    }
    if (trimmed.length > 50) {
      setRenameListError('Name must be 50 characters or fewer.');
      return;
    }
    if (!renameCustomList || !editingListId) {
      closeRenameListModal();
      return;
    }
    await renameCustomList(editingListId, trimmed);
    closeRenameListModal();
  };

  // Build list tiles: current, wishlist, then custom1/custom2 if they have a custom name or any products
  const custom1Count = getProductsByCategory('custom1').length;
  const custom2Count = getProductsByCategory('custom2').length;
  const showCustom1 = customListNames.custom1 !== 'My Custom List 1' || custom1Count > 0;
  const showCustom2 = customListNames.custom2 !== 'My Custom List 2' || custom2Count > 0;
  const canAddMoreLists = !showCustom1 || !showCustom2;

  const productLists = [
    {
      id: 'current',
      title: 'Current products',
      description: 'What you use now. Building this list helps Son:mi give you better advice.',
      icon: <Heart className="h-5 w-5" />,
      count: getProductsByCategory('current').length,
      color: '#F3D5B5'
    },
    {
      id: 'wishlist',
      title: 'Wishlist',
      description: "Products you're considering. Add items and get AI compatibility when you need it.",
      icon: <ShoppingCart className="h-5 w-5" />,
      count: wishlistProducts.length,
      color: '#F2EAE1'
    },
    ...(showCustom1 ? [{
      id: 'custom1',
      title: customListNames.custom1 || 'My Custom List 1',
      description: 'Your list, your name—e.g. Favourites, Travel, Gifts.',
      icon: <FolderPlus className="h-5 w-5" />,
      count: custom1Count,
      color: '#E8F4FD',
      isCustom: true
    }] : []),
    ...(showCustom2 ? [{
      id: 'custom2',
      title: customListNames.custom2 || 'My Custom List 2',
      description: 'Your list, your name—e.g. Favourites, Travel, Gifts.',
      icon: <FolderPlus className="h-5 w-5" />,
      count: custom2Count,
      color: '#F3E8FF',
      isCustom: true
    }] : [])
  ];

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3 w-3 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-xs text-[#2A324B]/70 ml-1">{rating}</span>
      </div>
    );
  };

  const renderLoadingSkeleton = () => (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-md bg-[#F3D5B5]/30" />
        <div className="flex-1">
          <div className="h-5 w-48 bg-[#2A324B]/10 rounded" />
          <div className="h-4 w-64 mt-2 bg-[#2A324B]/5 rounded" />
        </div>
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-lg bg-[#2A324B]/5" />
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-4 w-32 bg-[#2A324B]/10 rounded" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-16 rounded-lg bg-[#2A324B]/5" />
        ))}
      </div>
    </div>
  );

  const renderMainView = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors"
          aria-label="Back to menu"
        >
          <ChevronLeft className="h-5 w-5 text-[#2A324B]" />
        </button>
        <div>
          <h2 className="text-lg font-medium text-[#2A324B]">Analyse a Product</h2>
          <p className="text-sm text-[#2A324B]/70">Analyse or compare, then add what you like to your list.</p>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Featured: Product Analysis */}
        <div 
          className="border-2 border-[#F3D5B5] bg-gradient-to-br from-[#F3D5B5]/20 to-[#F2EAE1]/20 cursor-pointer hover:border-[#F3D5B5]/80 transition-all shadow-sm rounded-lg p-4"
          onClick={() => {
            setAnalysisProduct(null);
            setCurrentView('product-analysis');
          }}
        >
          <div className="flex gap-3 items-start">
            <div className="w-12 h-12 bg-[#F3D5B5] rounded-xl flex items-center justify-center flex-shrink-0">
              <Scan className="h-6 w-6 text-[#2A324B]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[#2A324B] mb-1">Analyze a product</h3>
              <p className="text-xs text-[#2A324B]/70 leading-relaxed mb-2">
                Snap or upload any product. Get an instant compatibility check for your skin and routine.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs bg-white/60 text-[#2A324B] px-2 py-0.5 rounded flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Instant Results
                </span>
                <span className="text-xs bg-white/60 text-[#2A324B] px-2 py-0.5 rounded flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Personalised
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button
              className="w-full bg-[#F3D5B5] hover:bg-[#F3D5B5]/80 text-[#2A324B] shadow-sm py-2 rounded-md flex items-center justify-center gap-2 text-sm font-medium"
              onClick={(e) => {
                e.stopPropagation();
                setAnalysisProduct(null);
                setCurrentView('product-analysis');
              }}
            >
              <Camera className="h-4 w-4" />
              Analyze Now
            </button>
          </div>
        </div>

        {/* Featured: Product Comparison */}
        <div 
          className="border-2 border-[#F2EAE1] bg-gradient-to-br from-[#F2EAE1]/20 to-[#F8F0E5]/20 cursor-pointer hover:border-[#F2EAE1]/80 transition-all shadow-sm rounded-lg p-4"
          onClick={() => {
            setCurrentView('product-comparison');
          }}
        >
          <div className="flex gap-3 items-start">
            <div className="w-12 h-12 bg-[#F2EAE1] rounded-xl flex items-center justify-center flex-shrink-0">
              <ArrowRightLeft className="h-6 w-6 text-[#2A324B]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[#2A324B] mb-1">Compare products</h3>
              <p className="text-xs text-[#2A324B]/70 leading-relaxed mb-2">
                Put 2–4 products side by side. See which one fits you best.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs bg-white/60 text-[#2A324B] px-2 py-0.5 rounded flex items-center gap-1">
                  <Brain className="h-3 w-3" />
                  AI-Powered
                </span>
                <span className="text-xs bg-white/60 text-[#2A324B] px-2 py-0.5 rounded flex items-center gap-1">
                  <Camera className="h-3 w-3" />
                  Photo Upload
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button
              className="w-full bg-[#F2EAE1] hover:bg-[#F2EAE1]/80 text-[#2A324B] shadow-sm py-2 rounded-md flex items-center justify-center gap-2 text-sm font-medium"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentView('product-comparison');
              }}
            >
              <ArrowRightLeft className="h-4 w-4" />
              Compare Now
            </button>
          </div>
        </div>

        {/* Your product lists */}
        <div>
          <h3 className="text-sm font-medium text-[#2A324B]/80 mb-3">Your lists</h3>
          <div className="space-y-2">
            {productLists.map((list) => (
              <div
                key={list.id}
                className="border border-[#341C1C]/10 hover:border-[#341C1C]/20 transition-colors cursor-pointer rounded-lg bg-white p-3"
                onClick={() => setCurrentView(list.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: list.color }}
                    >
                      {list.icon}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#2A324B]">
                        {list.title}
                      </p>
                      <p className="text-xs text-[#2A324B]/60">
                        {list.count} {list.count === 1 ? 'item' : 'items'}
                      </p>
                    </div>
                  </div>
                  <ChevronLeft className="h-4 w-4 text-[#2A324B]/40 rotate-180" aria-hidden="true" />
                </div>
              </div>
            ))}
          </div>
          
          {/* Add another list — only when a slot is free */}
          {canAddMoreLists ? (
            <button
              type="button"
              onClick={() => { setAddListError(''); setShowAddListModal(true); }}
              className="w-full mt-3 p-3 border-2 border-dashed border-[#341C1C]/20 hover:border-[#F3D5B5] hover:bg-[#F3D5B5]/5 transition-all rounded-lg bg-white text-[#2A324B]/70 hover:text-[#2A324B] flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add another list
            </button>
          ) : (
            <p className="w-full mt-3 p-3 text-center text-xs text-[#2A324B]/60 rounded-lg bg-[#F2EAE1]/30">
              You've reached the limit (2 custom lists). Delete one to add a new list.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const [addProductCategory, setAddProductCategory] = useState('current');

  const handleAddProduct = async (product) => {
    try {
      await saveProduct(product, addProductCategory);
      setShowAddForm(false);
      setAddProductCategory('current'); // Reset after save
    } catch (error) {
      console.error('Error adding product:', error);
    }
  };

  const handleOpenAddForm = (category) => {
    setAddProductCategory(category);
    setShowAddForm(true);
  };

  const handleEditProduct = async (product) => {
    try {
      await updateProduct(editingProduct.id, product);
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleProductSave = async (updatedProduct) => {
    try {
      await updateProduct(selectedProduct.id, updatedProduct);
      // selectedProduct will be synced via useEffect when savedProducts prop updates
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleProductDelete = async (productId) => {
    try {
      await deleteProduct(productId);
      setSelectedProduct(null);
      setCurrentView(sourceListId);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleAnalysisComplete = async (productId, analysisResult) => {
    // Update the product with AI analysis
    console.log('AI Analysis completed for product:', productId, analysisResult);
    
    // Find the product in local state
    let product = null;
    let productCategory = null;
    
    Object.keys(savedProducts).forEach(category => {
      if (Array.isArray(savedProducts[category])) {
        const found = savedProducts[category].find(p => p.id === productId);
        if (found) {
          product = found;
          productCategory = category;
        }
      }
    });
    
    if (!product) {
      console.error('❌ Product not found in local state');
      return;
    }
    
    try {
      // Try to update the product in the backend
      await updateProduct(productId, {
        ai_analysis: analysisResult
      });
      console.log('✅ Analysis saved to existing product');
    } catch (error) {
      // If update fails (404), product doesn't exist in database yet
      // Save it first, then the analysis will be included
      console.log('⚠️ Product not in database yet, saving it now...');
      
      try {
        await saveProduct({
          ...product,
          ai_analysis: analysisResult
        }, productCategory);
        console.log('✅ Product saved to database with analysis');
      } catch (saveError) {
        console.error('❌ Failed to save product:', saveError);
      }
    }
  };

  const renderCurrentProducts = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setCurrentView('lists')}
            className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors"
            aria-label="Back to catalog"
          >
            <ChevronLeft className="h-5 w-5 text-[#2A324B]" />
          </button>
          <div>
            <h2 className="text-lg font-medium text-[#2A324B]">Current products</h2>
            <p className="text-sm text-[#2A324B]/70">{getProductsByCategory('current').length} items in your routine</p>
          </div>
        </div>
        <button
          onClick={() => handleOpenAddForm('current')}
          className="flex items-center gap-2 px-3 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-lg hover:bg-[#F3D5B5]/80 transition-colors text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      <div className="space-y-3">
        {getProductsByCategory('current').length > 0 ? (
          getProductsByCategory('current').map((product) => (
            <div key={product.id} className="border border-[#341C1C]/10 bg-white rounded-lg p-4">
              <div className="flex gap-3 items-start">
                <div 
                  className="flex gap-3 cursor-pointer flex-1 min-w-0"
                  onClick={() => {
                    setSelectedProduct(product);
                    setSourceListId('current');
                    setCurrentView('product-detail');
                  }}
                >
                  <ProductImage product={product} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-[#2A324B] text-sm leading-tight">{product.name}</h3>
                        <p className="text-xs text-[#2A324B]/70 mt-0.5">{product.brand}</p>
                        {product.rating && renderStars(product.rating)}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium text-[#2A324B]">{product.price || ''}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-[#F3D5B5]/30 text-[#2A324B] px-2 py-1 rounded">
                        {product.category || 'Product'}
                      </span>
                      {product.usage && (
                        <div className="flex items-center gap-1 text-xs text-[#2A324B]/70">
                          <Clock className="h-3 w-3" />
                          {product.usage}
                        </div>
                      )}
                    </div>
                    
                    {product.notes && !product.ai_analysis && (
                      <p className="text-xs text-[#2A324B]/70 mt-2 italic">"{product.notes}"</p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setProductToDelete({ product, listId: 'current' })}
                  className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-md text-[#2A324B]/50 hover:text-red-600 hover:bg-red-50"
                  aria-label={`Remove ${product.name} from list`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div onClick={(e) => e.stopPropagation()}>
                {(() => {
                  let analysis = null;
                  if (product.ai_analysis) {
                    try {
                      analysis = typeof product.ai_analysis === 'string' 
                        ? JSON.parse(product.ai_analysis) 
                        : product.ai_analysis;
                    } catch (e) {
                      console.error('Failed to parse AI analysis:', e);
                    }
                  }
                  if (analysis && analysis.score !== undefined && analysis.compatibility) {
                    return (
                      <div className={`mt-3 p-3 rounded-lg border ${
                        analysis.compatibility === 'high' 
                          ? 'bg-green-50 border-green-200'
                          : analysis.compatibility === 'medium'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Brain className="h-4 w-4 text-[#2A324B]" />
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-[#2A324B]">
                              AI Analysis: {analysis.score}% Match
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              analysis.compatibility === 'high' 
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : analysis.compatibility === 'medium'
                                ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                                : 'bg-red-100 text-red-700 border border-red-300'
                            }`}>
                              {analysis.compatibility.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-[#2A324B]/80 leading-relaxed">
                          {analysis.reasoning}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <ProductAnalysisButton
                      product={product}
                      onAnalysisComplete={handleAnalysisComplete}
                      userId={userId}
                    />
                  );
                })()}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#F3D5B5] rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-[#2A324B]" />
            </div>
            <h3 className="text-lg font-medium text-[#2A324B] mb-2">No products yet</h3>
            <p className="text-[#2A324B]/70 max-w-sm mx-auto mb-6">
              Start saving products from your conversations to build your personal catalogue.
            </p>
            <button
              onClick={() => handleOpenAddForm('current')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-lg hover:bg-[#F3D5B5]/80 transition-colors font-medium"
            >
              <Plus className="h-4 w-4" />
              Add Product
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderWishlistProducts = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setCurrentView('lists')}
            className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors"
            aria-label="Back to catalog"
          >
            <ChevronLeft className="h-5 w-5 text-[#2A324B]" />
          </button>
          <div>
            <h2 className="text-lg font-medium text-[#2A324B]">Wishlist</h2>
            <p className="text-sm text-[#2A324B]/70">{wishlistProducts.length} items · Get AI compatibility when you need it</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => handleOpenAddForm('wishlist')}
          className="flex items-center gap-2 px-3 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-lg hover:bg-[#F3D5B5]/80 transition-colors text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      <div className="space-y-3">
        {wishlistProducts.length > 0 ? wishlistProducts.map((product) => (
          <div key={product.id} className="border border-[#341C1C]/10 bg-white rounded-lg p-4">
            <div className="flex gap-3 items-start">
              <div 
                className="flex gap-3 cursor-pointer flex-1 min-w-0"
                onClick={() => {
                  setSelectedProduct(product);
                  setSourceListId('wishlist');
                  setCurrentView('product-detail');
                }}
              >
                <ProductImage product={product} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-[#2A324B] text-sm leading-tight">{product.name}</h3>
                      <p className="text-xs text-[#2A324B]/70 mt-0.5">{product.brand}</p>
                      {product.rating && renderStars(product.rating)}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-[#2A324B]">{product.price || ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-xs bg-[#F3D5B5]/30 text-[#2A324B] px-2 py-1 rounded">
                      {product.category}
                    </span>
                    {product.usage && (
                      <div className="flex items-center gap-1 text-xs text-[#2A324B]/70">
                        <Clock className="h-3 w-3" />
                        {product.usage}
                      </div>
                    )}
                  </div>
                  
                  {/* Only show notes if there's NO AI analysis (to avoid duplication) */}
                  {product.notes && !product.ai_analysis && (
                    <p className="text-xs text-[#2A324B]/70 mt-2 italic">"{product.notes}"</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setProductToDelete({ product, listId: 'wishlist' })}
                className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-md text-[#2A324B]/50 hover:text-red-600 hover:bg-red-50"
                aria-label={`Remove ${product.name} from list`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* AI Analysis Results or Button */}
            <div onClick={(e) => e.stopPropagation()}>
              {(() => {
                // Parse AI analysis if it exists
                let analysis = null;
                if (product.ai_analysis) {
                  try {
                    analysis = typeof product.ai_analysis === 'string' 
                      ? JSON.parse(product.ai_analysis) 
                      : product.ai_analysis;
                  } catch (e) {
                    console.error('Failed to parse AI analysis:', e);
                  }
                }

                // If analysis exists and has data, show results
                if (analysis && analysis.score !== undefined && analysis.compatibility) {
                  return (
                    <div className={`mt-3 p-3 rounded-lg border ${
                      analysis.compatibility === 'high' 
                        ? 'bg-green-50 border-green-200'
                        : analysis.compatibility === 'medium'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Brain className="h-4 w-4 text-[#2A324B]" />
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[#2A324B]">
                            AI Analysis: {analysis.score}% Match
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            analysis.compatibility === 'high' 
                              ? 'bg-green-100 text-green-700 border border-green-300'
                              : analysis.compatibility === 'medium'
                              ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                              : 'bg-red-100 text-red-700 border border-red-300'
                          }`}>
                            {analysis.compatibility.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-[#2A324B]/80 leading-relaxed">
                        {analysis.reasoning}
                      </p>
                    </div>
                  );
                }

                // Otherwise show the analysis button
                return (
                    <ProductAnalysisButton
                      product={product}
                      onAnalysisComplete={handleAnalysisComplete}
                      userId={userId}
                    />
                );
              })()}
            </div>
          </div>
        )) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#F2EAE1] rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="h-8 w-8 text-[#2A324B]" />
            </div>
            <h3 className="text-lg font-medium text-[#2A324B] mb-2">No wishlist items yet</h3>
            <p className="text-[#2A324B]/70 max-w-sm mx-auto mb-6">
              Add products you're considering and get AI compatibility when you ask.
            </p>
            <button
              type="button"
              onClick={() => handleOpenAddForm('wishlist')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#F3D5B5] text-[#2A324B] rounded-lg hover:bg-[#F3D5B5]/80 transition-colors font-medium"
            >
              <Plus className="h-4 w-4" />
              Add to wishlist
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const getAllProducts = () => {
    const currentProducts = getProductsByCategory('current');
    const wishlist = getProductsByCategory('wishlist');
    const custom1 = getProductsByCategory('custom1');
    const custom2 = getProductsByCategory('custom2');
    return [...currentProducts, ...wishlist, ...custom1, ...custom2];
  };

  const renderCustomList = (list) => {
    const products = getProductsByCategory(list.id);
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
          <button
            type="button"
            onClick={() => setCurrentView('lists')}
            className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors"
            aria-label="Back to catalog"
          >
            <ChevronLeft className="h-5 w-5 text-[#2A324B]" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-medium text-[#2A324B]">{list.name}</h2>
            <p className="text-sm text-[#2A324B]/70">
              {products.length} {products.length === 1 ? 'item' : 'items'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => openRenameListModal(list.id)}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-[#F5F5F5] text-[#2A324B]"
            title="Rename list"
            aria-label="Rename list"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteCustomList(list.id)}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-red-50 text-red-600"
            title="Delete list"
            aria-label="Delete list"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: list.color }}
              >
                <FolderPlus className="h-8 w-8 text-[#2A324B]" />
              </div>
              <h3 className="text-lg font-medium text-[#2A324B] mb-2">No products yet</h3>
              <p className="text-sm text-[#2A324B]/70 mb-4">
                Add products to your "{list.name}" list
              </p>
              <button
                type="button"
                onClick={() => handleOpenAddForm(list.id)}
                className="px-4 py-2 bg-[#F3D5B5] hover:bg-[#F3D5B5]/80 text-[#2A324B] rounded-md flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Product
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="border border-[#341C1C]/10 bg-white rounded-lg p-4 flex gap-3"
                >
                  <div
                    className="flex gap-3 cursor-pointer flex-1 min-w-0"
                    onClick={() => {
                      setSelectedProduct(product);
                      setSourceListId(list.id);
                      setCurrentView('product-detail');
                    }}
                  >
                    <ProductImage product={product} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-[#2A324B] text-sm leading-tight">{product.name}</h3>
                          <p className="text-xs text-[#2A324B]/70 mt-0.5">{product.brand}</p>
                          <span className="inline-block text-xs bg-[#F3D5B5]/20 text-[#2A324B] px-2 py-0.5 rounded mt-1">
                            {product.category}
                          </span>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {product.rating && product.rating > 0 ? (
                            <div className="flex items-center gap-1 mb-1">
                              {renderStars(product.rating)}
                              <span className="text-xs text-[#2A324B]/70">{product.rating}</span>
                            </div>
                          ) : null}
                          {/* 2026-06-09: price hidden (we don't surface pricing) */}
                          {product.ai_analysis && (() => {
                            try {
                              const analysis = typeof product.ai_analysis === 'string' ? JSON.parse(product.ai_analysis) : product.ai_analysis;
                              if (typeof analysis?.score !== 'number') return null;
                              return (
                                <span className="inline-block text-[13px] font-semibold text-[#2A324B] bg-[#F3D5B5]/40 px-2 py-0.5 rounded-full">
                                  {analysis.score}%
                                </span>
                              );
                            } catch (_) { return null; }
                          })()}
                        </div>
                      </div>

                      {/* 2026-06-09 (Sofia #9): removed the verbose green
                          "AI Analysis: <reasoning paragraph>" card. Score now
                          shows as a tiny chip in the top-right; user taps the
                          product to see the full analysis. */}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setProductToDelete({ product, listId: list.id })}
                    className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-md text-[#2A324B]/50 hover:text-red-600 hover:bg-red-50 self-start"
                    aria-label={`Remove ${product.name} from list`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {showAddForm && (
        <AddProductForm
          onSave={handleAddProduct}
          onCancel={() => setShowAddForm(false)}
          targetCategory={addProductCategory}
          customLists={[
            ...(showCustom1 ? [{ id: 'custom1', name: customListNames.custom1 || 'My Custom List 1' }] : []),
            ...(showCustom2 ? [{ id: 'custom2', name: customListNames.custom2 || 'My Custom List 2' }] : [])
          ]}
        />
      )}
      
      {editingProduct && (
        <AddProductForm
          product={editingProduct}
          onSave={handleEditProduct}
          onCancel={() => setEditingProduct(null)}
        />
      )}
      
      {currentView === 'product-detail' && selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          sourceListId={sourceListId}
          onBack={() => {
            setSelectedProduct(null);
            setCurrentView(sourceListId);
          }}
          onSave={handleProductSave}
          onDelete={handleProductDelete}
          onAnalysisComplete={handleAnalysisComplete}
          onCompare={() => setCurrentView('product-comparison')}
          onAddToRoutine={(prod) => onOpenRoutine?.(prod)}
          userId={userId}
        />
      )}

      {currentView === 'product-analysis' && (
        <ProductAnalysisView
          onBack={() => initialView === 'product-analysis' ? onBack() : setCurrentView('lists')}
          existingProduct={analysisProduct || undefined}
          onSave={saveProduct}
          onContinueToChat={onContinueToChat}
          userId={userId}
          customLists={[
            ...(showCustom1 ? [{ id: 'custom1', name: customListNames.custom1 || 'My Custom List 1' }] : []),
            ...(showCustom2 ? [{ id: 'custom2', name: customListNames.custom2 || 'My Custom List 2' }] : [])
          ]}
          targetList={initialAnalyseTargetList}
          createCustomList={createCustomList}
          onGatedAction={onGatedAction}
        />
      )}
      
      {currentView === 'product-comparison' && (
        <ProductComparisonView
          onBack={() => initialView === 'product-comparison' ? onBack() : setCurrentView('lists')}
          availableProducts={getAllProducts()}
          userId={userId}
          saveProduct={saveProduct}
          onContinueToChat={onContinueToChat}
          isAnonymous={isAnonymous}
          onShowSignIn={onShowSignIn}
          onOpenCatalog={() => setCurrentView('lists')}
          onGatedAction={onGatedAction}
        />
      )}
      
      {currentView === 'current' && renderCurrentProducts()}
      {currentView === 'wishlist' && renderWishlistProducts()}
      {currentView === 'lists' && (isProductsLoading ? renderLoadingSkeleton() : (
        <ShelfView
          products={savedProducts}
          onBack={onBack}
          onSelectProduct={(p) => { setSelectedProduct(p); setSourceListId('lists'); setCurrentView('product-detail'); }}
          onMenuProduct={(p) => setMenuProduct(p)}
          onOpenRoutine={onOpenRoutine}
        />
      ))}
      
      {currentView === 'custom1' && renderCustomList({ id: 'custom1', name: customListNames.custom1 || 'My Custom List 1', color: '#E8F4FD' })}
      {currentView === 'custom2' && renderCustomList({ id: 'custom2', name: customListNames.custom2 || 'My Custom List 2', color: '#F3E8FF' })}

      {menuProduct && (
        <ShelfMenu
          product={menuProduct}
          onClose={() => setMenuProduct(null)}
          onViewDetails={() => { setSelectedProduct(menuProduct); setSourceListId('lists'); setCurrentView('product-detail'); setMenuProduct(null); }}
          onEditTags={() => { setSelectedProduct(menuProduct); setSourceListId('lists'); setCurrentView('product-detail'); setMenuProduct(null); }}
          onAddToRoutine={() => { const prod = menuProduct; setMenuProduct(null); onOpenRoutine?.(prod); }}
          onRemove={() => { setProductToDelete({ product: menuProduct }); setMenuProduct(null); }}
        />
      )}
      
      {productToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-medium text-[#2A324B] mb-2">Remove from list?</h3>
            <p className="text-sm text-[#2A324B]/70 mb-4">
              This will remove &quot;{productToDelete.product.name}&quot; from your list. You can add it again later.
            </p>
            {deleteError && (
              <p className="text-sm text-red-600 mb-2" role="alert">{deleteError}</p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setProductToDelete(null); setDeleteError(null); }}
                className="flex-1 px-4 py-2 border border-[#341C1C]/20 text-[#2A324B] rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setDeleteError(null);
                  try {
                    await deleteProduct(productToDelete.product.id);
                    setProductToDelete(null);
                  } catch (err) {
                    setDeleteError('Could not remove product. Please try again.');
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {editingListId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#2A324B]">Rename list</h3>
              <button
                type="button"
                onClick={closeRenameListModal}
                className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2A324B] mb-2">
                  List name
                </label>
                <input
                  type="text"
                  value={editingListName}
                  onChange={(e) => { setEditingListName(e.target.value); setRenameListError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCustomList(); if (e.key === 'Escape') closeRenameListModal(); }}
                  maxLength={50}
                  placeholder="e.g., Favourites, Travel, Gifts"
                  className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] focus:border-transparent"
                  autoFocus
                />
              </div>
              {renameListError && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {renameListError}
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeRenameListModal}
                  className="flex-1 px-4 py-2 border border-[#341C1C]/20 text-[#2A324B] rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRenameCustomList}
                  disabled={!editingListName.trim()}
                  className="flex-1 px-4 py-2 bg-[#F3D5B5] hover:bg-[#F3D5B5]/80 text-[#2A324B] rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddListModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#2A324B]">Create new list</h3>
              <button
                type="button"
                onClick={() => { setShowAddListModal(false); setNewListName(''); setAddListError(''); }}
                className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2A324B] mb-2">
                  List name
                </label>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => { setNewListName(e.target.value); setAddListError(''); }}
                  placeholder="e.g., Favourites, Travel, Gifts"
                  className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] focus:border-transparent"
                  autoFocus
                />
              </div>
              {addListError && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {addListError}
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddListModal(false); setNewListName(''); setAddListError(''); }}
                  className="flex-1 px-4 py-2 border border-[#341C1C]/20 text-[#2A324B] rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateCustomList}
                  disabled={!newListName.trim()}
                  className="flex-1 px-4 py-2 bg-[#F3D5B5] hover:bg-[#F3D5B5]/80 text-[#2A324B] rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create list
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCatalog;
